/**
 * InstanceListRow - Componente de linha para exibição de instância em formato lista
 * Exibe informações da instância em colunas horizontais com ações no final
 * 
 * ARQUITETURA:
 * - Instância = Motor de mensagens (pode ser vinculado a uma inbox)
 * - Cada instância está ligada a um provider, plano e pagamento
 * - Status: pending, released (disponível), in_use, cancelled
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  MessageCircle, 
  Instagram, 
  MessagesSquare, 
  Send, 
  Globe,
  MoreVertical,
  Link2,
  Unlink,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Package,
  CreditCard,
  Inbox,
  Trash2,
  ArrowRightLeft,
  RotateCcw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Instance, InstanceStatus, PaymentStatus, SourceType } from '@/hooks/useInstances';

/**
 * Props do componente InstanceListRow
 */
interface InstanceListRowProps {
  instance: Instance;
  onLinkToInbox?: (instance: Instance) => void;
  onUnlink?: (instance: Instance) => void;
  onViewDetails?: (instance: Instance) => void;
  onCancelInstance?: (instance: Instance) => void;
  onMigrateProvider?: (instance: Instance) => void;
  /** Exclusão definitiva da instância (apenas quando não está em uso) */
  onDeleteInstance?: (instance: Instance) => void;
  /** Reativação de instância cancelada (v1.6.0 — preserva credenciais/inbox) */
  onReactivate?: (instance: Instance) => void;
  hasReleasedInstances?: boolean;
  isLinking?: boolean;
  isUnlinking?: boolean;
  isReactivating?: boolean;
}

// Configuração de ícones por tipo de canal
const channelIconConfig: Record<string, { icon: typeof MessageCircle; color: string; bgColor: string }> = {
  whatsapp: { icon: MessageCircle, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-500/10' },
  whatsapp_oficial: { icon: MessageCircle, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' },
  instagram: { icon: Instagram, color: 'text-pink-600 dark:text-pink-400', bgColor: 'bg-pink-500/10' },
  messenger: { icon: MessagesSquare, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' },
  telegram: { icon: Send, color: 'text-sky-500 dark:text-sky-400', bgColor: 'bg-sky-400/10' },
  webchat: { icon: Globe, color: 'text-muted-foreground', bgColor: 'bg-muted' },
};

// Estilos de status da instância
const statusConfig: Record<InstanceStatus, { label: string; style: string; icon: typeof CheckCircle2 }> = {
  pending: { 
    label: 'Pendente', 
    style: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    icon: Clock
  },
  released: { 
    label: 'Disponível', 
    style: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    icon: CheckCircle2
  },
  in_use: { 
    label: 'Em Uso', 
    style: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    icon: Inbox
  },
  cancelled: { 
    label: 'Cancelada', 
    style: 'bg-muted text-muted-foreground border-muted',
    icon: XCircle
  },
  suspended: {
    label: 'Suspensa',
    style: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
    icon: AlertTriangle
  },
};

// Estilos de status de pagamento
const paymentStatusConfig: Record<PaymentStatus, { label: string; style: string }> = {
  pending: { label: 'Pendente', style: 'bg-amber-500/10 text-amber-600' },
  processing: { label: 'Processando', style: 'bg-blue-500/10 text-blue-600' },
  paid: { label: 'Pago', style: 'bg-green-500/10 text-green-600' },
  failed: { label: 'Falhou', style: 'bg-destructive/10 text-destructive' },
  refunded: { label: 'Reembolsado', style: 'bg-muted text-muted-foreground' },
};

// Labels de origem
const sourceTypeLabels: Record<SourceType, string> = {
  plan: 'Plano',
  addon: 'Add-on',
  manual: 'Manual',
};

/**
 * Componente de linha para exibição de instância
 */
export function InstanceListRow({ 
  instance, 
  onLinkToInbox, 
  onUnlink,
  onViewDetails,
  onCancelInstance,
  onMigrateProvider,
  onDeleteInstance,
  onReactivate,
  hasReleasedInstances = false,
  isLinking = false,
  isUnlinking = false,
  isReactivating = false,
}: InstanceListRowProps) {
  // Configurações com fallback para evitar undefined
  const channelConfig = channelIconConfig[instance.channel_type] || channelIconConfig.whatsapp;
  const ChannelIcon = channelConfig.icon;
  const status = statusConfig[instance.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const paymentStatus = paymentStatusConfig[instance.payment_status] || paymentStatusConfig.pending;
  
  // Instância suspensa bloqueia todas as ações de gestão
  const isSuspended = instance.status === 'suspended';
  // Determina se pode vincular (apenas instâncias released)
  const canLink = instance.status === 'released';
  // Determina se pode desvincular (apenas instâncias in_use)
  const canUnlink = instance.status === 'in_use' && instance.inbox_id;
  // Determina se pode cancelar (tem subscription e não está cancelada/suspensa)
  const canCancel = !!instance.tenant_addon_id && !['cancelled', 'suspended'].includes(instance.status) && instance.source_type === 'addon';
  // Determina se pode migrar provider (in_use e existem instâncias released com provider diferente)
  const canMigrateProvider = instance.status === 'in_use' && hasReleasedInstances;
  // Determina se pode excluir definitivamente (nunca quando vinculada a inbox)
  const canDelete = instance.status !== 'in_use' && !instance.inbox_id;

  return (
    <div className="flex items-center gap-2 sm:gap-4 px-2 py-2 sm:px-4 sm:py-3 hover:bg-muted/50 transition-colors">
      {/* Ícone do Canal */}
      <div className={cn(
        "p-1.5 sm:p-2 rounded-lg shrink-0",
        channelConfig.bgColor
      )}>
        <ChannelIcon className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4", channelConfig.color)} />
      </div>

      {/* Nome da Instância */}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-xs sm:text-sm truncate">{instance.instance_name}</p>
        {/* Provider + código visíveis apenas no mobile (colunas dedicadas em sm+) */}
        <p className="text-[10px] sm:hidden text-muted-foreground truncate">
          {instance.provider_display_name}
        </p>
      </div>

      {/* Coluna Cód. Instância dedicada (facilita identificação no suporte) */}
      <div className="hidden sm:flex items-center w-24 shrink-0">
        <Badge
          variant="outline"
          className="font-mono text-[10px] px-1.5 py-0 h-5 text-muted-foreground border-muted-foreground/30"
        >
          {instance.id.substring(0, 8).toUpperCase()}
        </Badge>
      </div>

      {/* Coluna Provider dedicada (mesmo padrão visual da aba Inboxes) */}
      <div className="hidden sm:flex items-center w-32 shrink-0">
        <Badge variant="outline" className="text-[10px] bg-muted/50 font-normal">
          via {instance.provider_display_name}
        </Badge>
      </div>

      {/* Inbox Vinculada */}
      <div className="hidden md:flex items-center gap-2 w-44 shrink-0">
        {instance.inbox_id && instance.inbox_name ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 truncate max-w-[180px]">
                  <Inbox className="h-3 w-3 mr-1 shrink-0" />
                  {instance.inbox_name}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Vinculada à inbox: {instance.inbox_name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : instance.status === 'released' ? (
          <Badge variant="outline" className="text-[10px] bg-green-500/5 text-green-600 dark:text-green-400 border-green-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Sem Inbox
          </Badge>
        ) : (
          <span className="text-[10px] text-muted-foreground">-</span>
        )}
      </div>

      {/* Origem (Plan/Addon) */}
      <div className="hidden lg:flex items-center w-24 shrink-0">
        <Badge variant="outline" className="text-[10px] bg-muted/50">
          <Package className="h-3 w-3 mr-1" />
          {sourceTypeLabels[instance.source_type]}
        </Badge>
      </div>

      {/* Subscription Stripe - Exibe se houver (v1.1.0: usa flags boolean) */}
      <div className="hidden xl:flex items-center w-28 shrink-0">
        {instance.has_subscription ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
                  <CreditCard className="h-3 w-3 mr-1" />
                  Ativa
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {instance.addon_name && <p className="text-xs text-muted-foreground">Add-on: {instance.addon_name}</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-muted/50 text-muted-foreground">
            <CreditCard className="h-3 w-3 mr-1" />
            {paymentStatusConfig[instance.payment_status]?.label || 'N/A'}
          </Badge>
        )}
      </div>

      {/* Status da Instância */}
      <div className="w-28 shrink-0">
        <Badge 
          variant="outline" 
          className={cn("text-[10px] px-1.5 sm:px-2 py-0.5 border", status.style)}
        >
          <StatusIcon className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline">{status.label}</span>
        </Badge>
      </div>

      {/* Menu de Ações */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 shrink-0">
            <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Vincular a Inbox */}
          {canLink && onLinkToInbox && (
            <DropdownMenuItem 
              onClick={() => onLinkToInbox(instance)}
              disabled={isLinking}
            >
              {isLinking ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Vincular a Inbox
            </DropdownMenuItem>
          )}

          {/* Desvincular de Inbox */}
          {canUnlink && onUnlink && (
            <DropdownMenuItem 
              onClick={() => onUnlink(instance)}
              disabled={isUnlinking}
            >
              {isUnlinking ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4 mr-2" />
              )}
              Desvincular da Inbox
            </DropdownMenuItem>
          )}

          {/* Migrar Provider */}
          {canMigrateProvider && onMigrateProvider && (
            <DropdownMenuItem onClick={() => onMigrateProvider(instance)}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Migrar Provider
            </DropdownMenuItem>
          )}

          {/* Separador se houver ações acima */}
          {(canLink || canUnlink || canMigrateProvider) && (onViewDetails || canCancel) && <DropdownMenuSeparator />}

          {/* Ver Detalhes */}
          {onViewDetails && (
            <DropdownMenuItem onClick={() => onViewDetails(instance)}>
              <AlertCircle className="h-4 w-4 mr-2" />
              Ver Detalhes
            </DropdownMenuItem>
          )}

          {/* Cancelar Instância (apenas add-ons com subscription) */}
          {canCancel && onCancelInstance && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onCancelInstance(instance)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cancelar Assinatura
              </DropdownMenuItem>
            </>
          )}

          {/* Mensagem para instância pendente */}
          {instance.status === 'pending' && (
            <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
              <Clock className="h-4 w-4 mr-2" />
              Aguardando liberação
            </DropdownMenuItem>
          )}

          {/* Reativar instância cancelada (v1.6.0 — preserva credenciais/inbox) */}
          {instance.status === 'cancelled' && onReactivate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onReactivate(instance)}
                disabled={isReactivating}
              >
                {isReactivating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                )}
                Reativar Instância
              </DropdownMenuItem>
            </>
          )}
          {instance.status === 'cancelled' && !onReactivate && (
            <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
              <XCircle className="h-4 w-4 mr-2" />
              Instância cancelada
            </DropdownMenuItem>
          )}

          {/* Mensagem para instância suspensa */}
          {isSuspended && (
            <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Regularize o pagamento
            </DropdownMenuItem>
          )}

          {/* Excluir Instância (definitiva) — apenas quando não está em uso */}
          {canDelete && onDeleteInstance && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDeleteInstance(instance)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Instância
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
